import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { approveBoothApplication, rejectBoothApplication, setPendingBoothApplication, deleteBoothApplication, listApplicationsForEvent, getBoothApplication, sendApprovalEmail } from '../services/applicationsService';
import { listEvents } from '../services/eventsService';
import { getLayoutPublic } from '../services/layoutsService';
import { computeBoothPrice } from '../config/boothPricing';
import { getBusinessTypes } from '../services/lookupsService';
import { listUserProfiles, getUserProfile } from '../services/profilesService';
import { useAuth } from '../context/AuthContext';
import { getVendor } from '../services/vendorsService';

const ApplicationsAdmin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [sendEmailOnApprove, setSendEmailOnApprove] = useState(true);
  const [filter, setFilter] = useState('Pending');
  const [vendorById, setVendorById] = useState({});
  // Selection and bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  // Event/Layout selection (Open + layout published)
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [publishedEvents, setPublishedEvents] = useState([]); // [{eventId,eventName,layoutId,layoutName}]
  const [eventId, setEventId] = useState('');
  const [hasSelectedEvent, setHasSelectedEvent] = useState(false);
  const [layoutId, setLayoutId] = useState('');
  const [eventName, setEventName] = useState('');
  const [layoutName, setLayoutName] = useState('');
  // Filters (similar to VendorsAdmin)
  const [qName, setQName] = useState(searchParams.get('name') || ''); // vendor name or contact name
  const [qEmail, setQEmail] = useState(searchParams.get('email') || ''); // vendor or contact email
  const [qTypes, setQTypes] = useState(() => {
    const t = searchParams.get('types');
    return t ? t.split(',').filter(Boolean) : [];
  });
  const [businessTypes, setBusinessTypes] = useState([]);
  const [profilesByUid, setProfilesByUid] = useState({});
  const [showDashboard, setShowDashboard] = useState(true);
  const [eventApps, setEventApps] = useState([]); // all apps for selected event (unfiltered)
  const [eventVendorById, setEventVendorById] = useState({});
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [modalVendorData, setModalVendorData] = useState(null);
  const [modalProfiles, setModalProfiles] = useState({});
  const { currentUser } = useAuth();

  const load = async () => {
    if (!hasSelectedEvent || !eventId) {
      setApps([]);
      setVendorById({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let loaded = [];
      // Load only applications for selected event
      loaded = await listApplicationsForEvent(eventId, { limitCount: 2000 });
      if (filter && filter !== 'All') loaded = loaded.filter(a => a.status === filter);
      setApps(loaded);
      const ids = Array.from(new Set((loaded || []).map(a => a.vendorId).filter(Boolean)));
      if (ids.length) {
        const vendors = await Promise.all(ids.map(id => getVendor(id).catch(() => null)));
        const map = {};
        vendors.forEach(v => { if (v && v.id) map[v.id] = v; });
        setVendorById(map);
      } else {
        setVendorById({});
      }
    } catch (e) {
      setError(e?.message || 'Failed to load applications');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, eventId, hasSelectedEvent]);

  // Load all applications for the selected event (unfiltered) to drive the dashboard counts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasSelectedEvent || !eventId) { setEventApps([]); setEventVendorById({}); return; }
      try {
        const all = await listApplicationsForEvent(eventId, { limitCount: 5000 });
        if (cancelled) return;
        setEventApps(all || []);
        const ids = Array.from(new Set((all || []).map(a => a.vendorId).filter(Boolean)));
        if (ids.length) {
          const vendors = await Promise.all(ids.map(id => getVendor(id).catch(() => null)));
          const map = {};
          vendors.forEach(v => { if (v && v.id) map[v.id] = v; });
          if (!cancelled) setEventVendorById(map);
        } else {
          if (!cancelled) setEventVendorById({});
        }
      } catch (e) {
        if (!cancelled) { setEventApps([]); setEventVendorById({}); }
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, hasSelectedEvent]);

  // Sync event/layout with query params when changed
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (hasSelectedEvent && eventId) params.set('eventId', eventId); else params.delete('eventId');
    if (hasSelectedEvent && layoutId) params.set('layoutId', layoutId); else params.delete('layoutId');
    setSearchParams(params, { replace: true });
  }, [eventId, layoutId, hasSelectedEvent]);

  // Load open events. Include events that are Open even if they don't have a layoutId.
  // When an event has a layoutId, only include it if the layout is published (existing behavior).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEventsLoading(true);
        setEventsError('');
        const all = await listEvents();
        let open = (all || []).filter(ev => {
          const s = (ev?.status || '').toLowerCase();
          return s.includes('open');
        });

        // Restrict visible events based on role:
        // - Admin: see full "open" list
        // - EventAdmin: see open events where their UID is in `eventAdmins`
        // - Organizer: see open events where their UID is in `organizers`
        // - Others: see none
        try {
          if (currentUser && currentUser.uid) {
            try {
              const prof = await getUserProfile(currentUser.uid);
              const uidLower = String(currentUser.uid || '').toLowerCase();
              if (prof?.userType === 'EventAdmin') {
                const filtered = (open || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower));
                open = filtered.length > 0 ? filtered : [];
              } else if (prof?.userType === 'Organizer') {
                const filtered = (open || []).filter(ev => {
                  const organizerList =
                    (Array.isArray(ev?.organizers) && ev.organizers) ||[];
                  return organizerList.some(a => String(a || '').trim().toLowerCase() === uidLower);
                });
                open = filtered.length > 0 ? filtered : [];
              } else if (prof?.userType !== 'Admin') {
                open = [];
              }
            } catch (profileErr) {
              open = [];
            }
          }
        } catch (e) {
          open = [];
        }

        const entries = await Promise.all(open.map(async (ev) => {
          // If event has a layoutId, ensure layout is published before including
          if (ev?.layoutId) {
            try {
              const ly = await getLayoutPublic(ev.layoutId);
              if ((ly?.status || '').toLowerCase() !== 'published') return null;
              return {
                eventId: ev.id,
                eventName: ev.name || ev.id,
                layoutId: ly.id,
                layoutName: ly.name || ly.title || ly.id,
                approvalEmailText: ev.approvalEmailText || '',
                eventAdminContactEmail: ev.eventAdminContactEmail || '',
              };
            } catch {
              return null;
            }
          }

          // No layoutId: include the Open event without layout metadata
          return {
            eventId: ev.id,
            eventName: ev.name || ev.id,
            layoutId: '',
            layoutName: '',
            approvalEmailText: ev.approvalEmailText || '',
            eventAdminContactEmail: ev.eventAdminContactEmail || '',
          };
        }));

        const filtered = entries.filter(Boolean).sort((a, b) => String(a.eventName).localeCompare(String(b.eventName)));
        if (!cancelled) {
          setPublishedEvents(filtered);
          if (eventId) {
            const match = filtered.find(p => p.eventId === eventId);
            if (match) {
              setEventName(match.eventName);
              if (!layoutId) setLayoutId(match.layoutId);
              setLayoutName(match.layoutName);
            } else {
              setEventId('');
              setHasSelectedEvent(false);
              setLayoutId('');
              setEventName('');
              setLayoutName('');
              setApps([]);
              setEventApps([]);
              setVendorById({});
              setEventVendorById({});
            }
          }
        }
      } catch (e) {
        if (!cancelled) setEventsError('Failed to load events');
      } finally { if (!cancelled) setEventsLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Layout name fetch if not in cached list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!layoutId) return;
      const existing = publishedEvents.find(p => p.layoutId === layoutId);
      if (existing) { setLayoutName(existing.layoutName); return; }
      try { const ly = await getLayoutPublic(layoutId); if (!cancelled) setLayoutName(ly?.name || ly?.title || layoutId); } catch { if (!cancelled) setLayoutName(layoutId); }
    })();
    return () => { cancelled = true; };
  }, [layoutId, publishedEvents]);

  const selectedEventMeta = useMemo(() => {
    return (publishedEvents || []).find(p => p.eventId === eventId) || null;
  }, [publishedEvents, eventId]);

  const approve = async (id) => {
    if (!window.confirm('Approve this application?')) return;
    await approveBoothApplication(id, note);
    setNote('');
    // send email to vendor if enabled
      try {
        if (sendEmailOnApprove) {
          // Verify the application status after the update before sending email
          const updated = await getBoothApplication(id);
          if (updated && String(updated.status || '').toLowerCase() === 'approved') {
            const app = updated;  // Use fresh data, not stale local app state
            const vendor = app?.vendorId ? vendorById[app.vendorId] : null;
            if (vendor?.email) {
              sendApprovalEmail(app, vendor, {
                eventName,
                approvalEmailText: selectedEventMeta?.approvalEmailText || '',
                eventAdminContactEmail: selectedEventMeta?.eventAdminContactEmail || '',
              });
            }
          }
        }
      } catch (e) {
        console.warn('Failed to send approval email', e);
      }
    await load();
  };
  const reject = async (id) => {
    if (!window.confirm('Reject this application?')) return;
    await rejectBoothApplication(id, note);
    setNote('');
    await load();
  };
  const setPending = async (id) => {
    if (!window.confirm('Set this application to Pending?')) return;
    await setPendingBoothApplication(id, note);
    setNote('');
    await load();
  };
  const deleteApp = async (id) => { 
    if (!window.confirm('Delete this application? This cannot be undone.')) return;
    await deleteBoothApplication(id); 
    await load(); 
  };
  
  const bulkApprove = async () => {
    setBulkMsg('');
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { setBulkMsg('Select one or more applications.'); return; }
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const id of ids) {
      try {
        await approveBoothApplication(id, note || '');
        ok++;
        // send email per-item if enabled
        try {
          if (sendEmailOnApprove) {
            // Verify the application status after update
            const updated = await getBoothApplication(id);
            if (updated && String(updated.status || '').toLowerCase() === 'approved') {
              const app = updated;  // Use fresh data, not stale local app state
              const vendor = app?.vendorId ? vendorById[app.vendorId] : null;
              if (vendor?.email) {
                sendApprovalEmail(app, vendor, {
                  eventName,
                  approvalEmailText: selectedEventMeta?.approvalEmailText || '',
                  eventAdminContactEmail: selectedEventMeta?.eventAdminContactEmail || '',
                });
              }
            }
          }
        } catch (e) {
          console.warn('Failed to send bulk approval email for', id, e);
        }
      } catch { fail++; }
    }
    setBulkBusy(false);
    setBulkMsg(`Approved ${ok} app(s)${fail ? `, ${fail} failed` : ''}.`);
    setSelectedIds(new Set());
    setNote('');
    await load();
  };
  const bulkReject = async () => {
    setBulkMsg('');
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { setBulkMsg('Select one or more applications.'); return; }
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const id of ids) {
      try { await rejectBoothApplication(id, note || ''); ok++; } catch { fail++; }
    }
    setBulkBusy(false);
    setBulkMsg(`Rejected ${ok} app(s)${fail ? `, ${fail} failed` : ''}.`);
    setSelectedIds(new Set());
    setNote('');
    await load();
  };
  const bulkSetPending = async () => {
    setBulkMsg('');
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { setBulkMsg('Select one or more applications.'); return; }
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const id of ids) {
      try { await setPendingBoothApplication(id, note || ''); ok++; } catch { fail++; }
    }
    setBulkBusy(false);
    setBulkMsg(`Set ${ok} app(s) to Pending${fail ? `, ${fail} failed` : ''}.`);
    setSelectedIds(new Set());
    setNote('');
    await load();
  };
  const bulkDelete = async () => {
    setBulkMsg('');
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { setBulkMsg('Select one or more applications.'); return; }
    if (!window.confirm(`Delete ${ids.length} application(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const id of ids) {
      try { await deleteBoothApplication(id); ok++; } catch { fail++; }
    }
    setBulkBusy(false);
    setBulkMsg(`Deleted ${ok} app(s)${fail ? `, ${fail} failed` : ''}.`);
    setSelectedIds(new Set());
    setNote('');
    await load();
  };

  const bulkResendApprovalEmails = async () => {
    setBulkMsg('');
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { setBulkMsg('Select one or more applications.'); return; }
    // Only resend for approved applications
    const approvedIds = ids.filter(id => {
      const app = apps.find(a => a.id === id);
      return app && String(app.status || '').toLowerCase() === 'approved';
    });
    if (approvedIds.length === 0) { setBulkMsg('No approved applications selected.'); return; }
    let ok = 0; let fail = 0;
    for (const id of approvedIds) {
      try {
        const app = apps.find(a => a.id === id);
        const vendor = app?.vendorId ? vendorById[app.vendorId] : null;
        if (vendor?.email) {
          sendApprovalEmail(app, vendor, {
            eventName,
            approvalEmailText: selectedEventMeta?.approvalEmailText || '',
            eventAdminContactEmail: selectedEventMeta?.eventAdminContactEmail || '',
          });
          ok++;
        } else {
          fail++;
        }
      } catch { fail++; }
    }
    alert(`Approval emails sent: ${ok}${fail ? `\nSkipped (no vendor email): ${fail}` : ''}.`);
  };

  // Load business types once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const bt = await getBusinessTypes(); if (!cancelled) setBusinessTypes(bt || []); } catch { if (!cancelled) setBusinessTypes([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load user profiles for apps (needed for contact name/email filtering)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const allProfiles = await listUserProfiles();
        if (cancelled) return;
        const map = {};
        (allProfiles || []).forEach(p => { if (p?.id) map[p.id] = p; });
        setProfilesByUid(map);
      } catch { if (!cancelled) setProfilesByUid({}); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep query params in sync with filters
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (qName.trim()) params.set('name', qName.trim()); else params.delete('name');
    if (qEmail.trim()) params.set('email', qEmail.trim()); else params.delete('email');
    if (qTypes && qTypes.length) params.set('types', qTypes.join(',')); else params.delete('types');
    setSearchParams(params, { replace: true });
  }, [qName, qEmail, qTypes]);

  const rows = useMemo(() => {
    return apps.map(app => {
      const vendor = app.vendorId ? vendorById[app.vendorId] : null;
      const profile = app.userUid ? profilesByUid[app.userUid] : null;
      const vendorName = vendor?.name || '';
      const vendorEmail = vendor?.email || '';
      const vendorType = vendor?.type || '';
      const contactName = ((profile?.firstName || '') + ' ' + (profile?.lastName || '')).trim();
      const contactEmail = profile?.email || '';
      return {
        id: app.id,
        status: app.status,
        submittedAt: app.submittedAt,
        boothsRequested: app.boothsRequested || [],
        userUid: app.userUid || '',
        vendorId: app.vendorId || '',
        vendorName,
        vendorEmail,
        vendorPhone: vendor?.phoneNumber || '',
        vendorType,
        contactName,
        contactEmail,
      };
    });
  }, [apps, vendorById, profilesByUid]);

  const filtered = useMemo(() => {
    const n = qName.trim().toLowerCase();
    const e = qEmail.trim().toLowerCase();
    const tSet = new Set((qTypes || []).map(t => (t || '').toLowerCase()));
    return rows.filter(r => {
      const nameMatch = !n || r.vendorName.toLowerCase().includes(n) || r.contactName.toLowerCase().includes(n);
      const emailMatch = !e || r.vendorEmail.toLowerCase().includes(e) || r.contactEmail.toLowerCase().includes(e);
      const typeMatch = tSet.size === 0 || tSet.has((r.vendorType || '').toLowerCase());
      return nameMatch && emailMatch && typeMatch;
    });
  }, [rows, qName, qEmail, qTypes]);

  // Counts by booth itemType (treated as Business Type) with vendor dedup, position breakdowns and status counts
  // Uses `eventApps` when available so dashboard reflects the full event; falls back to `apps`.
  const countsByType = useMemo(() => {
    const sourceApps = (eventApps && eventApps.length) ? eventApps : apps || [];
    const vendorMap = (eventVendorById && Object.keys(eventVendorById).length) ? eventVendorById : vendorById || {};
    const map = {};
    (sourceApps || []).forEach(app => {
      const vendorId = app.vendorId || null;
      const status = (app.status || '').toString().trim().toLowerCase();
      const boothsArr = Array.isArray(app.boothsRequested) ? app.boothsRequested : [];
      if (boothsArr.length === 0) {
        // fallback: count unspecified booths from numBooths
        const n = Number.isFinite(app.numBooths) ? Number(app.numBooths) : 0;
        if (n > 0) {
          const key = 'Unspecified';
          if (!map[key]) map[key] = { vendors: new Set(), apps: new Set(), approvedApps: new Set(), pendingApps: new Set(), rejectedApps: new Set(), corner: 0, inline: 0, other: 0, total: 0, pending: 0, approved: 0, rejected: 0 };
          map[key].total += n;
          if (status === 'approved') map[key].approved += n;
          else if (status === 'rejected') map[key].rejected += n;
          else map[key].pending += n;
          if (vendorId) map[key].vendors.add(vendorId);
          // record this application for per-app counts
          map[key].apps.add(app.id);
          if (status === 'approved') map[key].approvedApps.add(app.id);
          else if (status === 'rejected') map[key].rejectedApps.add(app.id);
          else map[key].pendingApps.add(app.id);
        }
      } else {
        // For boothsRequested entries, count booth-level metrics but also track unique apps per type
        boothsArr.forEach(b => {
          const itemType = (b.itemType || 'Unspecified').toString().trim() || 'Unspecified';
          const key = itemType;
          const pos = (b.position || '').toString().trim().toUpperCase();
          if (!map[key]) map[key] = { vendors: new Set(), apps: new Set(), approvedApps: new Set(), pendingApps: new Set(), rejectedApps: new Set(), corner: 0, inline: 0, other: 0, total: 0, pending: 0, approved: 0, rejected: 0 };
          if (pos.includes('CORNER')) map[key].corner += 1;
          else if (pos.includes('INLINE')) map[key].inline += 1;
          else map[key].other += 1;
          map[key].total += 1;
          if (status === 'approved') map[key].approved += 1;
          else if (status === 'rejected') map[key].rejected += 1;
          else map[key].pending += 1;
          if (vendorId) map[key].vendors.add(vendorId);
          // add this application id to the per-type app sets (deduped)
          map[key].apps.add(app.id);
          if (status === 'approved') map[key].approvedApps.add(app.id);
          else if (status === 'rejected') map[key].rejectedApps.add(app.id);
          else map[key].pendingApps.add(app.id);
        });
      }
    });
    return Object.entries(map)
      .map(([type, v]) => ({ type, vendorCount: v.vendors.size, pendingApps: (v.pendingApps && v.pendingApps.size) ? v.pendingApps.size : 0, pending: v.pending || 0, approved: v.approved || 0, rejected: v.rejected || 0, approvedApps: (v.approvedApps && v.approvedApps.size) ? v.approvedApps.size : 0, corner: v.corner || 0, inline: v.inline || 0, other: v.other || 0, total: v.total || 0 }))
      .sort((a, b) => b.total - a.total || b.vendorCount - a.vendorCount || String(a.type).localeCompare(String(b.type)));
  }, [eventApps, eventVendorById, apps, vendorById]);

  // Totals across all item types (for footer row)
  const dashboardTotals = useMemo(() => {
    const initial = { vendorCount: 0, pending: 0, pendingApps: 0, approved: 0, rejected: 0, approvedApps: 0, corner: 0, inline: 0, other: 0, total: 0 };
    return (countsByType || []).reduce((acc, cur) => {
      acc.vendorCount += Number(cur.vendorCount || 0);
      acc.pendingApps += Number(cur.pendingApps || 0);
      acc.pending += Number(cur.pending || 0);
      acc.approved += Number(cur.approved || 0);
      acc.rejected += Number(cur.rejected || 0);
      acc.approvedApps += Number(cur.approvedApps || 0);
      acc.corner += Number(cur.corner || 0);
      acc.inline += Number(cur.inline || 0);
      acc.other += Number(cur.other || 0);
      acc.total += Number(cur.total || 0);
      return acc;
    }, initial);
  }, [countsByType]);

  // Compute unique application counts (deduped across types) for approved and pending apps
  const dashboardUniqueAppCounts = useMemo(() => {
    const sourceApps = (eventApps && eventApps.length) ? eventApps : apps || [];
    const approvedSet = new Set();
    const pendingSet = new Set();
    const totalSet = new Set();
    (sourceApps || []).forEach(a => {
      if (!a || !a.id) return;
      totalSet.add(a.id);
      const status = (a.status || '').toString().trim().toLowerCase();
      if (status === 'approved') approvedSet.add(a.id);
      else if (status === 'pending') pendingSet.add(a.id);
    });
    return { approved: approvedSet.size, pending: pendingSet.size, total: totalSet.size };
  }, [eventApps, apps]);

  // Total booths requested for the selected event (sums numBooths or boothsRequested lengths)
  const totalBoothsRequested = useMemo(() => {
    const sourceApps = (eventApps && eventApps.length) ? eventApps : apps || [];
    return (sourceApps || []).reduce((sum, app) => {
      const n = Number.isFinite(app.numBooths) ? Number(app.numBooths) : (Array.isArray(app.boothsRequested) ? app.boothsRequested.length : (app.boothsRequested ? 1 : 0));
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [eventApps, apps]);

  const clearFilters = () => {
    setQName('');
    setQEmail('');
    setQTypes([]);
  };

  // Selection helpers based on filtered list
  const allFilteredIds = useMemo(() => new Set(filtered.map(r => r.id)), [filtered]);
  const allSelectedInFilter = selectedIds.size > 0 && filtered.every(r => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    if (allSelectedInFilter) {
      const next = new Set(selectedIds);
      filtered.forEach(r => next.delete(r.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      filtered.forEach(r => next.add(r.id));
      setSelectedIds(next);
    }
  };

  return (
    <div className="profile-container profile-wide-container">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate('/')} title="Close">Close</button>
      </div>
      
      <h2 className="profile-title">Booth Applications</h2>
      {/* (Vendor / Contact header moved into vendor modal) */}
      {/* Event/Layout Selection */}
      <div className="profile-grid" style={{ marginBottom: 12 }}>
        <div>
          <label className="profile-label">Select Open Event </label>
          <select
            className="profile-input"
            value={eventId || ''}
            onChange={(e) => {
              const val = e.target.value;
              const opt = publishedEvents.find(p => p.eventId === val);
              setEventId(val);
              setHasSelectedEvent(Boolean(val));
              if (opt) {
                setEventName(opt.eventName);
                setLayoutId(opt.layoutId);
                setLayoutName(opt.layoutName);
              } else { setEventName(val); }
            }}
            disabled={eventsLoading || publishedEvents.length === 0}
          >
            {eventsLoading && <option value="">Loading events…</option>}
            {!eventsLoading && publishedEvents.length === 0 && <option value="">No open events</option>}
            {!eventsLoading && publishedEvents.length > 0 && (
              <>
                <option value="" disabled>Select an event…</option>
                {publishedEvents.map(p => (
                  <option key={p.eventId} value={p.eventId}>{p.eventName}</option>
                ))}
              </>
            )}
          </select>
          {eventId && <div className="profile-hint" style={{ marginTop: 4 }}>Event ID: {eventId}</div>}
          {eventsError && <div className="profile-error" style={{ marginTop: 4 }}>{eventsError}</div>}
        </div>
        <div>
          <label className="profile-label">Layout (Published)</label>
          <input className="profile-input" value={layoutName || (layoutId || '')} readOnly />
          {layoutId && <div className="profile-hint" style={{ marginTop: 4 }}>Layout ID: {layoutId}</div>}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="profile-hint">Selecting an event filters applications to that event. Clear selection to view status-wide list.</div>
        </div>
      </div>
      {!hasSelectedEvent || !eventId ? (
        <div style={{ marginBottom: 12 }}>
          <div className="profile-hint" style={{ fontSize: 14 }}>
            Select an event to load the dashboard and applications table.
          </div>
        </div>
      ) : (
        <>
          {/* Booth Applications By Event dashboard */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Booth Applications By Event</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div>
                <button
                  type="button"
                  className="back-btn"
                  onClick={() => setShowDashboard(s => !s)}
                  style={{ padding: '6px 10px', color: '#111', background: '#f0f0f0', border: '1px solid #d0d7de' }}
                >
                  {showDashboard ? 'Hide Dashboard' : 'Display Dashboard'}
                </button>
              </div>
            </div>
          </div>
          {showDashboard && (
            <div style={{ border: '1px solid #e6e6e6', borderRadius: 6, padding: 10, marginBottom: 12, background: '#fafafa' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}></th>
                <th colSpan={2} style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700 }}>Vendor Counts </th>
                <th colSpan={6} style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700 }}>Booth Counts</th>
              </tr>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Booth Category</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, width: 120 }}>Total Applications</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, width: 120 }}>Approved Applications</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, width: 120, borderRight: '2px solid #ddd' }}>Pending Applications</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, width: 80}}>Pending</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, width: 80 }}>Approved</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, width: 80, borderRight: '2px solid #ddd'  }}>Rejected</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, width: 80 }}>Corner</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, width: 80 }}>Inline</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, width: 100 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {countsByType.length === 0 && (
                <tr><td style={{ padding: '6px 8px' }} colSpan={8}>No applications</td></tr>
              )}
              {countsByType.map(ct => (
                <tr key={ct.type}>
                  <td style={{ padding: '6px 8px' }}>{ct.type}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>{ct.vendorCount}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>{ct.approvedApps || 0}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' , borderRight: '2px solid #ddd'}}>{ct.pendingApps || 0}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{ct.pending || 0}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{ct.approved || 0}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right',borderRight: '2px solid #ddd' }}>{ct.rejected || 0}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{ct.corner || 0}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{ct.inline || 0}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{ct.total || 0}</td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ borderTop: '2px solid #ddd', fontWeight: 700, background: '#fff' }}>
                <td style={{ padding: '6px 8px' }}>Totals</td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>{dashboardUniqueAppCounts.total}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>{dashboardUniqueAppCounts.approved}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', borderRight: '2px solid #ddd' }}>{dashboardUniqueAppCounts.pending}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{dashboardTotals.pending}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{dashboardTotals.approved}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right',borderRight: '2px solid #ddd'  }}>{dashboardTotals.rejected}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{dashboardTotals.corner}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{dashboardTotals.inline}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{dashboardTotals.total}</td>
              </tr>
            </tbody>
              </table>
            </div>
          )}
          {error && <div className="profile-error">{error}</div>}
          {/* Application Filters */}
          <div style={{ fontSize: 14, fontWeight: 700 }}>Use these filters to find and process vendor applications.</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>-</div>
          <div className="profile-grid" style={{ marginBottom: 12 }}>
        <div>
          <label className="profile-label">Vendor or Contact Name</label>
          <input value={qName} onChange={e => setQName(e.target.value)} className="profile-input" placeholder="e.g. Acme or Jane" />
        </div>
        <div>
          <label className="profile-label">Business Type (multi-select)</label>
          <select
            multiple
            value={qTypes}
            onChange={e => setQTypes(Array.from(e.target.selectedOptions).map(o => o.value))}
            className="profile-input"
            size={Math.min(6, Math.max(3, businessTypes.length))}
          >
            {businessTypes.map(bt => (
              <option key={bt} value={bt}>{bt}</option>
            ))}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            <div className="profile-hint">Hold Ctrl/Cmd to multi-select. Leave empty for all.</div>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => setQTypes([])}
              disabled={!qTypes.length}
              title="Unselect all business types"
              style={{ padding: '4px 10px' }}
            >Clear Types</button>
          </div>
        </div>
        <div>
          <label className="profile-label">Vendor or Contact Email</label>
          <input value={qEmail} onChange={e => setQEmail(e.target.value)} className="profile-input" placeholder="e.g. user@ex.com or biz@ex.com" />
        </div>
        <div style={{ display:'flex', alignItems:'flex-end', gap:8, flexWrap:'wrap' }}>
          <button
            type="button"
            className="back-btn"
            onClick={toggleSelectAll}
            style={{ color: '#111', background: '#fff', border: '1px solid #ddd' }}
          >
            {allSelectedInFilter ? 'Unselect All (Filtered)' : 'Select All (Filtered)'}
          </button>
          <button
            type="button"
            className="back-btn"
            disabled={selectedIds.size===0}
            onClick={() => setSelectedIds(new Set())}
            style={{ color: '#111', background: '#fff', border: '1px solid #ddd' }}
          >
            Clear Selection
          </button>
          <button
            type="button"
            className="back-btn"
            onClick={clearFilters}
            style={{ color: '#111', background: '#fff', border: '1px solid #ddd' }}
          >
            Clear Filters
          </button>
        </div>
        <div className="profile-hint" style={{ gridColumn:'1 / -1' }}>Showing {filtered.length} of {rows.length} applications.</div>
          </div>
          <div style={{display:'flex', gap:10, alignItems:'center', marginBottom: 10, flexWrap:'wrap'}}>
        <div style={{display:'flex', gap:10, alignItems:'center'}}>
          <label className="profile-label">Status</label>
          <select className="profile-input" value={filter} onChange={e => setFilter(e.target.value)}>
            {['All','Pending','Approved','Rejected'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <input type="checkbox" checked={sendEmailOnApprove} onChange={e => setSendEmailOnApprove(e.target.checked)} id="send-email-approve" />
            <label htmlFor="send-email-approve" style={{ fontSize: 13 }}>Send approval email to vendors</label>
          </div>
          <div style={{width:12}} />
          <input value={note} onChange={e => setNote(e.target.value)} className="profile-input" placeholder="Optional note (applies to bulk)" style={{minWidth:260, maxWidth:420}} />
        </div>
        <button type="button" className="apply-btn" disabled={bulkBusy || selectedIds.size===0} onClick={bulkApprove} title="Approve selected applications">{bulkBusy ? 'Working…' : `Approve Selected (${selectedIds.size})`}</button>
        <button type="button" className="apply-btn" disabled={bulkBusy || selectedIds.size===0} onClick={bulkReject} title="Reject selected applications">{bulkBusy ? 'Working…' : `Reject Selected (${selectedIds.size})`}</button>
        <button type="button" className="apply-btn" disabled={bulkBusy || selectedIds.size===0} onClick={bulkSetPending} title="Set selected applications to Pending">{bulkBusy ? 'Working…' : `Set to Pending (${selectedIds.size})`}</button>
        <button type="button" className="cancel-btn" disabled={bulkBusy || selectedIds.size===0} onClick={bulkDelete} title="Delete selected applications">{bulkBusy ? 'Working…' : `Delete Selected (${selectedIds.size})`}</button>
        <button type="button" className="apply-btn" disabled={bulkBusy || selectedIds.size===0} onClick={bulkResendApprovalEmails} title="Resend approval email to selected approved applications" style={{backgroundColor:'#0969da'}}>{bulkBusy ? 'Working…' : `Resend Approval Emails (${selectedIds.size})`}</button>
          </div>
          {bulkMsg && <div className="profile-hint" style={{color:'#2e7d32', marginBottom:12}}>{bulkMsg}</div>}
          {loading ? (
            <div>Loading…</div>
          ) : (
            <div style={{overflowX:'auto'}}>
              <table className="profile-table" style={{width:'100%', borderCollapse:'collapse'}}>
            <thead>
              <tr>
                <th style={{textAlign:'left', padding:'10px', width:36}}>
                  <input type="checkbox" checked={filtered.length>0 && allSelectedInFilter} onChange={toggleSelectAll} aria-label="Select all filtered" />
                </th>
                <th style={{textAlign:'left', padding:'10px'}}>Actions</th>
                <th style={{textAlign:'left', padding:'10px'}}>Submitted</th>
                <th style={{textAlign:'left', padding:'10px'}}>Vendor Name</th>
                <th style={{textAlign:'left', padding:'10px', width:120}}>Booths Requested</th>
                <th style={{textAlign:'left', padding:'10px'}}>Type</th>
                <th style={{textAlign:'left', padding:'10px'}}>Vendor Email</th>
                <th style={{textAlign:'left', padding:'10px'}}>Vendor Phone</th>
                <th style={{textAlign:'left', padding:'10px'}}>Status</th>
                {/* Requested column removed */}
                <th style={{textAlign:'left', padding:'10px'}}>Total Est.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(app => {
                const est = (app.boothsRequested || []).reduce((s, b) => {
                  // If estimate is present, it's stored in cents; convert to dollars.
                  if (typeof b.estimate === 'number' && !isNaN(b.estimate)) return s + (b.estimate / 100);
                  // Fallback: computeBoothPrice returns dollars.
                  return s + (computeBoothPrice(b.level, b.position) || 0);
                }, 0);
                return (
                  <tr key={app.id}>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>
                      <input type="checkbox" checked={selectedIds.has(app.id)} onChange={() => {
                        const next = new Set(selectedIds);
                        if (next.has(app.id)) next.delete(app.id); else next.add(app.id);
                        setSelectedIds(next);
                      }} aria-label={`Select application ${app.id}`} />
                    </td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>
                      <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                        {String(app.status || '').toLowerCase() !== 'approved' && (
                          <button 
                            type="button" 
                            className="apply-btn" 
                            onClick={() => approve(app.id)}
                            style={{padding:'4px 8px', fontSize:'12px'}}
                            title="Approve this application"
                          >
                            Approve
                          </button>
                        )}
                        {String(app.status || '').toLowerCase() !== 'rejected' && (
                          <button 
                            type="button" 
                            className="apply-btn" 
                            onClick={() => reject(app.id)}
                            style={{padding:'4px 8px', fontSize:'12px'}}
                            title="Reject this application"
                          >
                            Reject
                          </button>
                        )}
                        {String(app.status || '').toLowerCase() !== 'pending' && (
                          <button 
                            type="button" 
                            className="apply-btn" 
                            onClick={() => setPending(app.id)}
                            style={{padding:'4px 8px', fontSize:'12px'}}
                            title="Set to Pending status"
                          >
                            Pending
                          </button>
                        )}
                        <button 
                          type="button" 
                          className="cancel-btn" 
                          onClick={() => deleteApp(app.id)}
                          style={{padding:'4px 8px', fontSize:'12px'}}
                          title="Delete this application"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>{app.submittedAt?.toDate?.() ? app.submittedAt.toDate().toLocaleString() : ''}</td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>
                      {app.vendorName ? (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            (async () => {
                              try {
                                const vendor = app.vendorId ? vendorById[app.vendorId] : null;
                                // Fetch fresh application record to ensure updatedAt/updatedBy and review fields are current
                                let latestApp = app;
                                if (app?.id) {
                                  try {
                                    const fetched = await getBoothApplication(app.id);
                                    if (fetched) latestApp = fetched;
                                  } catch (err) {
                                    // ignore fetch errors and fall back to list item
                                  }
                                }
                                setModalVendorData({ vendor, app: latestApp });
                                setShowVendorModal(true);

                                // Fetch any member profiles not already loaded, and also fetch the application submitter profile
                                const memberUids = (vendor && Array.isArray(vendor.memberUids)) ? vendor.memberUids : [];
                                const toCheck = [...memberUids];
                                const submitterUid = latestApp?.userUid;
                                if (submitterUid) toCheck.push(submitterUid);
                                // Also fetch reviewer / updater profiles so we can show names instead of ids
                                const reviewerUid = latestApp?.reviewedBy;
                                const updaterUid = latestApp?.updatedBy;
                                if (reviewerUid) toCheck.push(reviewerUid);
                                if (updaterUid) toCheck.push(updaterUid);
                                const missing = toCheck.filter(uid => uid && !profilesByUid[uid] && !modalProfiles[uid]);
                                if (missing.length) {
                                  const results = await Promise.all(missing.map(uid => getUserProfile(uid).catch(() => null)));
                                  const map = {};
                                  results.forEach((p, i) => { if (p) map[missing[i]] = p; });
                                  setModalProfiles(prev => ({ ...prev, ...map }));
                                }
                              } catch (err) {
                                // ignore overall errors
                              }
                            })();
                          }}
                          style={{ color: '#0366d6', textDecoration: 'underline', fontWeight: 700 }}
                        >
                          {app.vendorName}
                        </a>
                      ) : '-'}
                    </td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee', textAlign:'center'}}>
                      {(() => {
                        const n = Array.isArray(app.boothsRequested) ? app.boothsRequested.length : (Number.isFinite(app.numBooths) ? Number(app.numBooths) : 0);
                        return n || n === 0 ? String(n) : '-';
                      })()}
                    </td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>{app.vendorType || '-'}</td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>{app.vendorEmail || '-'}</td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>{app.vendorPhone || '-'}</td>
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>{app.status}</td>
                    {/* Requested column removed */}
                    <td style={{padding:'10px', borderTop:'1px solid #eee'}}>{est ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(est) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
              </table>
              <div className="profile-actions" style={{ marginTop: 12, display:'flex', justifyContent:'flex-end' }}>
                <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
              </div>
            </div>
          )}
        </>
      )}

      {showVendorModal && modalVendorData && (
        <div className="validation-modal-overlay" onClick={() => setShowVendorModal(false)}>
          <div className="validation-modal" style={{ width: '900px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="validation-modal-header" style={{ backgroundColor: '#0366d6', color: 'white' }}>
              <h4 style={{ margin: 0 }}>Booth Application</h4>
              <button
                onClick={() => setShowVendorModal(false)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ffffff9c'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.95)'; e.currentTarget.style.boxShadow = 'none'; }}
                style={{ color: '#256dc0', backgroundColor: 'rgba(255, 255, 255, 0.75)', border: 'none', padding: '6px 10px', cursor: 'pointer', borderRadius: 6, fontWeight: 700 }}
                title="Close"
              >×</button>
            </div>
            <div className="validation-modal-content">
              {(() => {
                const { vendor, app } = modalVendorData || {};
                const memberUids = (vendor && Array.isArray(vendor.memberUids)) ? vendor.memberUids : [];
                const members = memberUids.map(uid => profilesByUid[uid] || modalProfiles[uid] || null).filter(Boolean);

                const formatAddressFromProfile = (p) => {
                  const parts = [];
                  if (p.businessAddressStreet) parts.push(p.businessAddressStreet);
                  if (p.businessAddressCity || p.businessAddressState) {
                    const cityLine = [p.businessAddressCity, p.businessAddressState].filter(Boolean).join(', ');
                    if (cityLine) parts.push(cityLine + (p.businessAddressZip ? (' ' + p.businessAddressZip) : ''));
                  }
                  // Fallback older fields
                  if (parts.length === 0) {
                    if (p.addressStreet) parts.push(p.addressStreet);
                    const cityLine2 = [p.addressCity, p.addressState].filter(Boolean).join(', ');
                    if (cityLine2) parts.push(cityLine2 + (p.addressZip ? (' ' + p.addressZip) : ''));
                  }
                  return parts.join('\n');
                };

                const submitter = (app && app.userUid) ? (profilesByUid[app.userUid] || modalProfiles[app.userUid] || null) : null;
                const submitterName = submitter ? (((submitter.firstName || '') + ' ' + (submitter.lastName || '')).trim() || submitter.contactName || submitter.email || submitter.id) : null;
                const reviewer = (app && app.reviewedBy) ? (profilesByUid[app.reviewedBy] || modalProfiles[app.reviewedBy] || null) : null;
                const reviewerName = reviewer ? (((reviewer.firstName || '') + ' ' + (reviewer.lastName || '')).trim() || reviewer.contactName || reviewer.email || reviewer.id) : null;
                const updater = (app && app.updatedBy) ? (profilesByUid[app.updatedBy] || modalProfiles[app.updatedBy] || null) : null;
                const updaterName = updater ? (((updater.firstName || '') + ' ' + (updater.lastName || '')).trim() || updater.contactName || updater.email || updater.id) : null;

                const headerMarkup = (
                  <>
                    <div style={{ display: 'flex', gap: 20, marginBottom: 12, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>Vendor Info</div>
                      <div style={{ fontWeight: 700, color: '#0366d6' }}>{vendor?.name || vendor?.businessName || app?.vendorName || '-'}</div>
                      <div> {vendor?.type || vendor?.businessType || app?.vendorType || '-'}</div>
                      <div> {vendor?.email || vendor?.businessEmail || app?.vendorEmail || '-'}</div>
                      <div> {vendor?.phoneNumber || '-'}</div>
                      {vendor?.addressStreet && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{vendor.addressStreet}</div>}
                      {(vendor?.addressCity || vendor?.addressState) && <div style={{ whiteSpace: 'pre-wrap' }}>{[vendor?.addressCity, vendor?.addressState].filter(Boolean).join(', ') + (vendor?.addressZip ? (' ' + vendor.addressZip) : '')}</div>}
                      {/* Requested Booths and Application Submission Info moved into Vendor Info column */}
                      <div style={{ height: 2, background: '#cfcfcf', margin: '12px 0', width: '100%' }} />
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Application Submission Info</div>
                        {app?.userUid && <div style={{ marginTop: 6 }}>Submitted By: {submitterName ? <span style={{ fontWeight: 700, color: '#0366d6' }}>{submitterName}</span> : '-'}</div>}
                        {app?.submittedAt && <div style={{ marginTop: 6, color: '#666' }}><small>Submitted: {app.submittedAt?.toDate ? app.submittedAt.toDate().toLocaleString() : String(app.submittedAt)}</small></div>}
                        {app?.id && <div style={{ marginTop: 6, color: '#666' }}><small>Application ID: {app.id}</small></div>}
                      </div>
                      {((app?.status && (String(app.status).toLowerCase() === 'approved' || String(app.status).toLowerCase() === 'rejected')) || app?.updatedAt || app?.updatedBy) && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ height: 2, background: '#cfcfcf', margin: '12px 0', width: '100%' }} />
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>Review / Update Info</div>
                          {app.status && <div style={{ marginBottom: 4 }}>Result: {app.status}</div>}
                          {app.reviewedBy && (
                            <div style={{ marginBottom: 4 }}>Reviewed By: {reviewerName || app.reviewedBy}</div>
                          )}
                          {app.reviewedAt && (
                            <div style={{ color: '#666', marginBottom: 4 }}>Reviewe Date: {app.reviewedAt?.toDate ? app.reviewedAt.toDate().toLocaleString() : String(app.reviewedAt)}</div>
                          )}
                          {app.reviewNote && (
                            <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {app.reviewNote}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>Contact Info</div>
                      {members.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {members.map((m, i) => (
                            <div key={m.id || i} style={{ whiteSpace: 'pre-wrap' }}>
                              <div style={{ fontWeight: 700, color: '#0366d6' }}>{(((m.firstName || '') + ' ' + (m.lastName || '')).trim()) }</div>
                              <div>{m.email || '-'}</div>
                              <div>{m.phoneNumber || '-'}</div>
                              {formatAddressFromProfile(m) && <div style={{ marginTop: 4 }}>{formatAddressFromProfile(m)}</div>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          <div style={{ fontWeight: 700, color: '#0366d6' }}>{vendor?.name || vendor?.businessName || '-'}</div>
                          <div>{vendor?.email ||'-'}</div>
                          <div>{vendor?.phoneNumber || '-'}</div>
                          {(() => {
                            const addrParts = [];
                            if (vendor?.addressStreet) addrParts.push(vendor.addressStreet);
                            if (vendor?.addressCity || vendor?.addressState) {
                              const cityLine = [vendor?.addressCity, vendor?.addressState].filter(Boolean).join(', ');
                              if (cityLine) addrParts.push(cityLine + (vendor?.addressZip ? (' ' + vendor.addressZip) : ''));
                            }
                            return addrParts.length ? addrParts.join('\n') : '-';
                          })()}
                        </div>
                      )}
                      {/* Review / Update info: moved into second column */}                     
                      <div style={{ height: 2, background: '#cfcfcf', margin: '12px 0', width: '100%' }} />
                      {app?.boothsRequested && app.boothsRequested.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>
                            Requested Booths{(() => {
                              const n = Array.isArray(app.boothsRequested) ? app.boothsRequested.length : (Number.isFinite(app.numBooths) ? Number(app.numBooths) : 0);
                              return n ? ` (${n})` : '';
                            })()}
                          </div>
                          {(app.boothsRequested || []).map((b, i) => {
                            const price = (typeof b.estimate === 'number' && !isNaN(b.estimate)) ? (b.estimate / 100) : (computeBoothPrice(b.level, b.position) || 0);
                            const priceLabel = price ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price) : '';
                            const posLabel = (b.position || '').toString();
                            const itemTypeLabel = (b.itemType || '-').toString();
                            const showOtherNotes = itemTypeLabel.trim().toLowerCase() === 'other' && String(b.notes || '').trim();
                            return (
                              <div key={i} style={{ marginBottom: 6 }}>
                                <div>
                                  {b.level || '-'} {' • '}
                                  {posLabel || '-'} {' • '}
                                  {itemTypeLabel || '-'} {' • '}
                                  {priceLabel || '-'}
                                </div>
                                {showOtherNotes && (
                                  <div style={{ marginTop: 4, marginLeft: 16, whiteSpace: 'pre-wrap' }}>
                                    Description : {b.notes}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    </div>
                  </>
                );

                return (
                  <div>
                    {headerMarkup}
                  </div>
                );
              })()}
            </div>
            <div className="validation-modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12 }}>
              <button type="button" className="back-btn" onClick={() => setShowVendorModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationsAdmin;
